import InlineFieldErrors from '@/components/form/InlineFieldErrors'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { CustomerSearchInput, renderCustomerOption } from '@/components/pos/CustomerSearchInput'
import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { ORDER_ASIDE_FORM_SURFACE_CLASS } from '@/components/orders/order-aside.styles'
import { Typography } from '@/components/ui/typography'
import { submitSchemaValidator, useAppForm } from '@/lib/form'
import type { Customer } from '@/lib/services/customers/customers.types'
import type {
  SalesOrder,
  SalesOrderExternalPurchaser,
  UpdateSalesOrderPurchaserInput,
} from '@/lib/services/sales-orders/sales-orders.types'
import { customerQueries } from '@/lib/services/customers/customers.query'
import { salesOrderPurchaserSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { useUpdateSalesOrderPurchaserMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { getErrorMessage } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import { UserRound } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

type EditOrderPurchaserFormProps = {
  order: Pick<SalesOrder, 'id' | 'organization_id' | 'purchaser_customer_id' | 'purchaser'>
  closeAside: () => void
}

type SelectedCustomer = Pick<Customer, 'id' | 'name' | 'email' | 'phone' | 'physical_address'>
type PurchaserSelection =
  | SelectedCustomer
  | Pick<SalesOrderExternalPurchaser, 'id' | 'name' | 'email' | 'phone'>

type PendingPurchaserChange = {
  customer: SelectedCustomer
  previousCustomerId: NonNullable<SalesOrder['purchaser_customer_id']>
  previousCustomerName: string
}

export function EditOrderPurchaserForm({ order, closeAside }: EditOrderPurchaserFormProps) {
  const [search, setSearch] = useState('')
  const [pendingChange, setPendingChange] = useState<PendingPurchaserChange | null>(null)
  const [submitError, setSubmitError] = useState<string | undefined>('')
  const queryClient = useQueryClient()
  const orgId = useRouteOrganizationIdParam()
  const mutation = useUpdateSalesOrderPurchaserMutation()
  const previousCustomerId = order.purchaser_customer_id ?? order.purchaser?.id ?? null

  const save = async (
    customer: Pick<Customer, 'id'>,
    confirmedPreviousCustomerId: UpdateSalesOrderPurchaserInput['previous_purchaser_customer_id']
  ) => {
    setSubmitError('')
    try {
      await mutation.mutateAsync({
        id: order.id,
        purchaser_customer_id: customer.id,
        previous_purchaser_customer_id: confirmedPreviousCustomerId,
      })
      toast.success('Purchaser updated')
      closeAside()
    } catch (error) {
      const message = getErrorMessage(error)
      toast.error('Purchaser update failed', { description: message })
      setSubmitError(message)
    }
  }

  const defaultValues: { customer: PurchaserSelection | null } = {
    customer: previousCustomerId ? order.purchaser : null,
  }
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: submitSchemaValidator(salesOrderPurchaserSchema) },
    onSubmit: async ({ value }) => {
      const customer = value.customer
      // The submit schema rejects an empty selection and external purchasers without a customer id.
      if (!customer || customer.id === null) return

      if (Number(customer.id) === Number(previousCustomerId)) {
        closeAside()
      } else if (previousCustomerId) {
        setPendingChange({
          customer,
          previousCustomerId,
          previousCustomerName: order.purchaser?.name || 'the current customer',
        })
      } else {
        await save(customer, null)
      }
    },
  })

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Purchaser"
          subtitle="Choose the customer for this order."
          icon={UserRound}
          tone="brand"
          className="shadow-none"
        >
          <div className={`${ORDER_ASIDE_FORM_SURFACE_CLASS} space-y-5`}>
            <form.AppField name="customer">
              {(field) => (
                <div className="grid gap-y-5">
                  <CustomerSearchInput
                    id="order-purchaser-search"
                    label="Find a customer"
                    placeholder="Search by phone number, name, or email..."
                    value={search}
                    onChange={setSearch}
                    fetchOptions={(searchInput) =>
                      queryClient.fetchQuery(
                        customerQueries.index({
                          organizationId: order.organization_id ?? orgId,
                          filters: { autocomplete: true, limit: 10, search: searchInput },
                        })
                      )
                    }
                    onOptionSelected={(customer) => {
                      field.handleChange(customer)
                      setSearch('')
                      setSubmitError('')
                    }}
                    renderOption={renderCustomerOption}
                    disabled={mutation.isPending}
                  />
                  {field.state.value ? (
                    <div className="space-y-2">
                      <Typography variant="eyebrow">Selected customer</Typography>
                      {renderCustomerOption(field.state.value)}
                    </div>
                  ) : null}
                  <InlineFieldErrors errors={[...field.state.meta.errors, submitError]} />
                </div>
              )}
            </form.AppField>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={mutation}
          saveLabel="Save Purchaser"
          onCancel={closeAside}
        />
      </form.Form>
      <ConfirmationDialog
        open={Boolean(pendingChange)}
        onOpenChange={(open) => {
          if (!open) setPendingChange(null)
        }}
        trigger={null}
        title="Change purchaser?"
        description={`This order will move from ${pendingChange?.previousCustomerName || 'the current customer'} to ${pendingChange?.customer.name || 'the selected customer'}. Any remaining balance will become their responsibility, the order will appear in their history, and new order notifications will go to them. Messages already queued still go to their original recipients.`}
        confirmText="Change Purchaser"
        isPending={mutation.isPending}
        onConfirm={
          pendingChange
            ? () => save(pendingChange.customer, pendingChange.previousCustomerId)
            : undefined
        }
      />
    </form.AppForm>
  )
}
